/**
 * Application bundles (#372): a farm's named recipes of products applied together. The server
 * recomputes every amount from the farm's own setting when it saves; this mirrors the arithmetic
 * only for the preview shown before Save.
 */

export interface BundleItem {
  product_id: string;
  /** Amount per hectare of pond, in the product's own unit. */
  per_hectare?: number;
  /** A fixed proportion of the first product's amount. */
  per_lead?: number;
  /** The first product only: the technician types this amount. */
  typed?: boolean;
}

export interface ApplicationBundle {
  id: string;
  name: string;
  items: BundleItem[];
}

export type BundleAmounts = { amounts: Array<{ product_id: string; amount: number }> } | { error: string };

// Settle float noise before rounding so the preview rounds half-up like the server's Decimal
const round3 = (x: number) => Math.round(Number((x * 1000).toFixed(6))) / 1000;

export function bundleAmounts(bundle: ApplicationBundle, areaHa: number | null, leadAmount: number | null): BundleAmounts {
  const needsArea = bundle.items.some((i) => i.per_hectare !== undefined);
  if (needsArea && !(areaHa !== null && areaHa > 0)) return { error: 'Enter the pond area (ha)' };
  const amounts: Array<{ product_id: string; amount: number }> = [];
  let lead = 0;
  for (const [index, item] of bundle.items.entries()) {
    let amount: number;
    if (item.per_hectare !== undefined) amount = round3(item.per_hectare * (areaHa as number));
    else if (item.typed) {
      if (!(leadAmount !== null && leadAmount > 0)) return { error: 'Type the amount of the first product' };
      amount = round3(leadAmount);
    } else amount = round3(lead * (item.per_lead ?? 0));
    if (index === 0) lead = amount;
    if (!(amount > 0) || amount > 9999999.999) return { error: 'An amount is out of range: check the pond area' };
    amounts.push({ product_id: item.product_id, amount });
  }
  return { amounts };
}

export function slugify(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'bundle';
}
